import * as React from 'react'
import classNames from 'classnames'

type AlignIndicator = 'left' | 'right'

export interface SwitchProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
    alignIndicator?: AlignIndicator
    inline?: boolean
    innerLabel?: React.ReactNode
    innerLabelChecked?: React.ReactNode
    label?: React.ReactNode
    large?: boolean
}

const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
    (
        {
            alignIndicator = 'left',
            checked,
            className,
            disabled,
            inline = false,
            innerLabel,
            innerLabelChecked,
            label,
            large = false,
            onChange,
            ...props
        },
        ref,
    ) => {
        const hasVisibleLabel = label != null
        const isControlled = checked !== undefined
        const [uncontrolledChecked, setUncontrolledChecked] = React.useState(
            Boolean(props.defaultChecked),
        )
        const currentChecked = isControlled ? Boolean(checked) : uncontrolledChecked

        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            if (!isControlled) {
                setUncontrolledChecked(event.target.checked)
            }
            onChange?.(event)
        }

        const indicator = (
            <span
                aria-hidden="true"
                className={classNames(
                    'relative inline-flex flex-none items-center rounded-full transition-colors',
                    currentChecked
                        ? 'bg-[#2d72d2] group-hover:bg-[#215db0] group-active:bg-[#184a90]'
                        : 'bg-[rgba(143,153,168,0.3)] group-hover:bg-[rgba(143,153,168,0.4)] group-active:bg-[rgba(143,153,168,0.5)]',
                    disabled &&
                        (currentChecked
                            ? 'cursor-not-allowed bg-[rgba(45,114,210,0.5)] group-hover:bg-[rgba(45,114,210,0.5)] group-active:bg-[rgba(45,114,210,0.5)]'
                            : 'cursor-not-allowed bg-[rgba(143,153,168,0.15)] group-hover:bg-[rgba(143,153,168,0.15)] group-active:bg-[rgba(143,153,168,0.15)]'),
                    large ? 'h-[20px] min-w-[35px]' : 'h-[16px] min-w-[28px]',
                )}
            >
                {(innerLabel || innerLabelChecked) && (
                    <span
                        className={classNames(
                            'absolute select-none text-[10px] font-normal leading-none text-white',
                            large ? 'left-[7px] right-[7px]' : 'left-[6px] right-[6px]',
                        )}
                    >
                        {currentChecked ? innerLabelChecked : innerLabel}
                    </span>
                )}
                <span
                    className={classNames(
                        'absolute left-[2px] rounded-full bg-white shadow-[0_0_0_1px_rgba(17,20,24,0.5)] transition-transform',
                        disabled &&
                            (currentChecked
                                ? 'bg-[rgba(255,255,255,0.5)] shadow-none'
                                : 'bg-[rgba(255,255,255,0.8)] shadow-none'),
                        large ? 'h-[16px] w-[16px]' : 'h-[12px] w-[12px]',
                        currentChecked && (large ? 'translate-x-[15px]' : 'translate-x-[12px]'),
                    )}
                />
            </span>
        )

        return (
            <label
                className={classNames(
                    'group cursor-pointer select-none items-center text-sm font-normal normal-case text-[#1c2127]',
                    inline ? 'inline-flex mr-[20px]' : 'flex mb-[10px]',
                    disabled && 'cursor-not-allowed opacity-50',
                    className,
                )}
            >
                <input
                    {...props}
                    ref={ref}
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={handleChange}
                    className="sr-only"
                />
                {alignIndicator === 'left' && indicator}
                {hasVisibleLabel && (
                    <span
                        className={classNames(
                            alignIndicator === 'left' ? 'ml-[10px]' : 'mr-[10px]',
                        )}
                    >
                        {label}
                    </span>
                )}
                {alignIndicator === 'right' && indicator}
            </label>
        )
    },
)
Switch.displayName = 'Switch'

export { Switch }
